import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { FilterSelectedValue } from '../../../hooks/filters/interfaces/filter-selected-value.interface';
import { AppFilterSelectedValue } from '../../../hooks/filters/use-app-filter-selected-value.hook';
import { FILTERS } from '../../../hooks/filters/use-app-filters.hook';
import { getFiltersService } from '../../../services/get-filters.service';
import {
  useFilterSelectionDateToReset,
  useFilterSelectionToReset,
} from './use-filter-panel-selection.hook';

/**
 * Reset all filters to default values.
 * @param onReset
 * @param filterSelection
 * @returns
 */
export const useFilterReset = (
  onReset?: () => void,
  filterSelection?: FilterSelectedValue[],
) => {
  const filterSetSelection = useFilterSelectionToReset();
  const handleResetFilter = useCallback(() => {
    if (filterSetSelection && filterSetSelection.length > 0) {
      getFiltersService(filterSelection).then((filters) => {
        filters.forEach((filter) => {
          const filterSel = filterSetSelection.find(
            (i) => i.selectedValue?.id === filter.id,
          );
          if (filterSel && filterSel.setSelectedValue !== undefined) {
            filterSel.setSelectedValue(filter.defaultSelectedValue);
          }
        });
        if (onReset) {
          onReset();
        }
      });
    }
  }, [filterSelection, filterSetSelection, onReset]);
  return {
    handleResetFilter,
  };
};

export const useFilterResetFunction = (
  filterSelection: FilterSelectedValue[],
  filterSetSelection: AppFilterSelectedValue[],
) => {
  const onSelectCallback = useCallback(() => {
    getFiltersService(filterSelection).then((filters) => {
      if (filters && filterSetSelection && filterSetSelection.length > 0) {
        filters.forEach((filter) => {
          const filterSel = filterSetSelection.find(
            (i) => i.selectedValue?.id === filter.id,
          );
          if (filterSel && filterSel.setSelectedValue !== undefined) {
            filterSel.setSelectedValue(filter.defaultSelectedValue);
          }
        });
      }
    });
  }, [filterSelection, filterSetSelection]);

  return onSelectCallback;
};
/**
 * When Meta Category changes, category filters must back to its default values.
 * @param filterSelection
 * @param filterId
 * @returns
 */
export const useFilterResetOnSelect = (
  filterSelection: FilterSelectedValue[],
  ...filterId: string[]
) => {
  const filterSetSelection = useFilterSelectionToReset();
  const onSelectFunction = useFilterResetFunction(
    filterSelection,
    filterSetSelection,
  );
  const onSelectCallback = useCallback(
    (selectedValue: FilterSelectedValue) => {
      if (filterId.indexOf(selectedValue.id) !== -1) {
        onSelectFunction();
      }
    },
    [filterId, onSelectFunction],
  );

  return onSelectCallback;
};
/**
 * When Market filter changes, last run date may change as well. Therefore, date filters must back to default values based on new last run date.
 *
 * @param filterSelection
 * @param filterId
 * @returns
 */
export const useFilterDateResetOnSelect = (
  filterSelection: FilterSelectedValue[],
  ...filterId: string[]
) => {
  const filterSetSelection = useFilterSelectionDateToReset();
  const onSelectFunction = useFilterResetFunction(
    filterSelection,
    filterSetSelection,
  );
  const onSelectCallback = useCallback(
    (selectedValue: FilterSelectedValue) => {
      if (filterId.indexOf(selectedValue.id) !== -1) {
        onSelectFunction();
      }
    },
    [filterId, onSelectFunction],
  );

  return onSelectCallback;
};

export const useFilterResetCallback = () => {
  const queryClient = useQueryClient();
  const filterSetSelection = useFilterSelectionToReset();

  const handleResetFilter = useCallback(
    async (filterSelection?: FilterSelectedValue[]) => {
      if (filterSetSelection && filterSetSelection.length > 0) {
        const filters = await queryClient.fetchQuery({
          queryKey: [FILTERS],
          queryFn: () => getFiltersService(filterSelection),
        });

        filters.forEach((filter) => {
          const filterSel = filterSetSelection.find(
            (i) => i.selectedValue?.id === filter.id,
          );

          if (filterSel && filterSel.setSelectedValue !== undefined) {
            filterSel.setSelectedValue(filter.defaultSelectedValue);
          }
        });
      }
    },
    [filterSetSelection, queryClient],
  );

  return {
    handleResetFilter,
  };
};
